/* Copyright (c) 2017 Backpack Health LLC */

import * as _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';

import ContentFrame from '../../../applicationFrame/ContentFrame';
import { ScrollAwareContext, ScrollAwareAppNavStep } from '../../../components/ScrollAware';

import ProfileName from './ProfileName';
import ProfileDateOfBirth from './ProfileDateOfBirth';
import ProfileGender from './ProfileGender.redux';
import ProfilePrivacyConsent, { ProfilePrivacyConsentClass } from './ProfilePrivacyConsent';
import PolicyDependentAuthorization, { PolicyDependentAuthorizationClass } from './PolicyDependentAuthorization/PolicyDependentAuthorization';

import ProfileModel from '../../../../models/ProfileModel';
import GenderModel from '../../../../models/GenderModel';
import ProfileActions from '../../redux/ProfileActions';
import ProfilePhrases from '../../../../phraseKeys/Profile.phrase';
import { updateStatusBarColor } from '../../../../utilities/MobileUtil';
import { ProfileSetupIntro } from './ProfileSetupIntro';
import ProfileService from '../../../../apiServices/ProfileService';
import ProfileResidence from './ProfileResidence';
import { ActionItemViewName } from '../../../../models/ActionItemModel';
import HealthInfoPrivacy, { HealthInfoPrivacyClass } from './HealthInfoPrivacy';
import DoNotConsentModal, { WithdrawName } from '../../../components/modal/DoNotConsentModal';
import ContentLoader from '../../../components/ContentLoader';
import { PolicyName } from '../../../../models/PolicyModel';

class DependentProfileCreation extends React.Component {

  constructor(props) {
    super(props);

    this.incrementStep = this.incrementStep.bind(this);
    this.decrementStep = this.decrementStep.bind(this);
    this.submitProfileChanges = this.submitProfileChanges.bind(this);
    this.onChangeProfile = this.onChangeProfile.bind(this);
    this.stepIsValid = this.stepIsValid.bind(this);
    this.consentToPolicies = this.consentToPolicies.bind(this);
    this.checkValidation = this.checkValidation.bind(this);

    const profile = this.initProfile();

    this.state = {
      profile,
      defaultOtherGenderText: props.intl.formatMessage({ id: 'list.gender.other.name' }),
      submitErrorText: '',
      errorMessage: '',
      currentStep: 0,
      isSubmitting: false,
      profileActionItems: [],
      agreedToPolicyPersonalInfo: false,
      agreedToPolicyHealthInfo: false,
      agreedToPolicyDependentAuth: false,
      fieldErrorName: '',
      steps: [
        {
          name: 'intro',
          onDisabledClick: null,
          cancel: this.props.cancelCreation,
          isValid: [],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: ProfileSetupIntro,
          phraseData: () => ProfilePhrases.dependentProfileSetupIntro(true),
          onWithdrawConsent: this.handleOnWithdrawConsent,
        },
        {
          name: ActionItemViewName.policy_dependent_authorization,
          submitDisabled: true,
          goBack: this.decrementStep,
          next: () => {
            this.setState({ agreedToPolicyDependentAuth: true }, this.incrementStep);
          },
          onDisabledClick: () => {
            PolicyDependentAuthorizationClass.scrollAcceptButtonIntoView();
          },
          isValid: [() => this.state.agreedToPolicyDependentAuth],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: PolicyDependentAuthorization,
          phraseData: () => ProfilePhrases.policyDependentAuth(false),
          title: () => ProfilePhrases.policyDependentAuth(false).title,
          onWithdrawConsent: () => { this.handleOnWithdrawConsent(PolicyName.DEPENDENT_AUTHORIZATION); },
        },
        {
          name: ActionItemViewName.policy_personal_info,
          goBack: this.decrementStep,
          next: () => {
            this.setState({ agreedToPolicyPersonalInfo: true }, this.incrementStep);
          },
          onDisabledClick: () => {
            ProfilePrivacyConsentClass.scrollAcceptButtonIntoView();
          },
          isValid: [() => this.state.agreedToPolicyPersonalInfo],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: ProfilePrivacyConsent,
          phraseData: () => ProfilePhrases.profilePrivacyConsent(),
          onWithdrawConsent: () => { this.handleOnWithdrawConsent(PolicyName.PRIVACY); },
        },
        {
          name: ActionItemViewName.profile_name,
          goBack: this.decrementStep,
          onDisabledClick: this.checkValidation,
          isValid: [() => this.state.profile.hasGivenOrFamilyName(), () => this.state.profile.hasPreferredName()],
          viewErrorMessage: ['error.name-validator.missing-given-family-names.message', 'error.name-validator.missing-preferred-name.message'],
          fieldErrorName: ['', ''],
          content: ProfileName,
          phraseData: () => ProfilePhrases.profileName(false),
          onWithdrawConsent: () => { this.handleOnWithdrawConsent(PolicyName.NAME); },
        },
        {
          name: ActionItemViewName.profile_residence,
          goBack: () => {
            this.setState({ currentStep: Math.max(this.state.currentStep - 1, 0) });
          },
          onDisabledClick: this.checkValidation,
          isValid: [() => {
            if (!this.state.profile.hasValidResidence()) {
              return false;
            } else if (this.props.isResidenceStateRequired(this.state.profile.residenceCountryId)) {
              return !!this.state.profile.residenceStateId;
            }

            return true;
          }],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: ProfileResidence,
          phraseData: () => ProfilePhrases.profileResidence(false, this.state.profile.greetingName()),
          onWithdrawConsent: () => { this.handleOnWithdrawConsent(PolicyName.RESIDENCE); },
        },
        {
          name: ActionItemViewName.profile_dob,
          goBack: this.decrementStep,
          onDisabledClick: this.checkValidation,
          isValid: [() => this.state.profile.hasValidDateOfBirth()],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: ProfileDateOfBirth,
          phraseData: () => ProfilePhrases.profileDateOfBirth(false),
          onWithdrawConsent: () => { this.handleOnWithdrawConsent(PolicyName.DATE_OF_BIRTH); },
        },
        {
          name: ActionItemViewName.profile_gender,
          goBack: this.decrementStep,
          onDisabledClick: this.checkValidation,
          isValid: [() => this.state.profile.hasValidGender(), () => this.state.profile.hasValidBirthSex()],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: ProfileGender,
          phraseData: () => ProfilePhrases.profileGender(false),
          onWithdrawConsent: () => { this.handleOnWithdrawConsent(PolicyName.GENDER); },
        },
        {
          name: ActionItemViewName.policy_health_info,
          goBack: this.decrementStep,
          next: () => {
            this.setState({ agreedToPolicyHealthInfo: true }, this.submitProfileChanges);
          },
          onDisabledClick: () => {
            HealthInfoPrivacyClass.scrollAcceptButtonIntoView();
          },
          isValid: [() => this.state.agreedToPolicyHealthInfo],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: HealthInfoPrivacy,
          phraseData: () => ProfilePhrases.healthInfo(),
          onWithdrawConsent: () => { this.handleOnWithdrawConsent(PolicyName.HEALTH_INFO); },
        },
      ],
    };
  }

  async UNSAFE_componentWillMount() {
    this.props.loadInitialData();
    const data = await ProfileService.getNewProfileActionItems();
    const steps = [...this.state.steps];
    const actionItemNames = data.steps.map(s => s.view);

    // filter out completed steps
    for (let i = 1; i < steps.length; i++) {
      if (steps[i].name && actionItemNames.indexOf(steps[i].name) < 0) {
        steps.splice(i, 1);
        i--;
      }
    }

    this.setState({ profileActionItems: data.steps, steps });
  }

  onChangeProfile(profileData) {
    this.setState({
      profile: new ProfileModel(profileData),
      fieldErrorName: '',
    });
  }

  clearFieldErrors = () => {
    this.setState({ fieldErrorName: '' });
  }

  initProfile() {
    const { mainProfile } = this.props;
    const { residence_country_id, residence_state_id } = mainProfile.data;

    return new ProfileModel({
      residence_country_id,
      residence_state_id,
    });
  }

  consentToPolicies(profileId, callback) {
    const { profileActionItems } = this.state;
    const policyList = _.flatten(profileActionItems.map(p => p.policy_id));

    if (policyList.length > 0) {
      ProfileActions.api.provideConsent(profileId, policyList).then(callback);
    }
  }

  checkValidation() {
    const validators = this.state.steps[this.state.currentStep].isValid;

    for (let i = 0; i < validators.length; i++) {
      if (!validators[i]()) {
        this.props.showHintNotification(this.state.steps[this.state.currentStep].viewErrorMessage[i]);
        return false;
      }
    }

    return true;
  }

  submitProfileChanges(e) {
    if (e) { e.preventDefault(); }

    if (!this.checkValidation()) { return; }

    if (this.state.isSubmitting) { return; }

    if (this.state.currentStep < (this.state.steps.length - 1)) {
      this.incrementStep();
      return;
    }

    this.setState({ isSubmitting: true }, () => {
      this.state.profile.cleanNames();
      this.state.profile.setColorThemeId(this.props.currentColorTheme.id);

      const profileWithValidValues = _.pickBy(this.state.profile.data, (val) => val !== null);
      const genderData = this.props.genderIdMap[profileWithValidValues.gender_id] || {};
      if (!GenderModel.isOther(genderData)) {
        profileWithValidValues.other_gender = '';
      }
      ProfileActions.api.createDependentProfile(profileWithValidValues, (apiError, newProfile) => {
        this.setState({ isSubmitting: false });
        this.consentToPolicies(newProfile.data.id, () => {
          if (apiError) {
            this.props.showNotification(apiError.messageKey());
          } else {
            this.props.resetAppMenu();
            this.props.finishProfileCreation(newProfile);
          }
        });
      });
    });
  }

  incrementStep() {
    this.setState({ currentStep: Math.min(this.state.currentStep + 1, this.state.steps.length) });
  }

  decrementStep() {
    this.setState({ currentStep: Math.max(this.state.currentStep - 1, 0) });
  }

  stepIsValid() {
    const validators = this.state.steps[this.state.currentStep].isValid;

    for (let i = 0; i < validators.length; i++) {
      if (!validators[i]()) {
        return false;
      }
    }

    if (this.props.isNotificationShown) {
      this.props.dismissNotification();
    }

    return true;
  }

  handleOnWithdrawConsent = (policyName) => {
    this.props.showSystemModal(<DoNotConsentModal withdrawName={WithdrawName.duringDependentSetup} policyName={policyName} isShared={false} />);
  }

  render() {
    updateStatusBarColor(this.props.currentColorTheme.base_color);

    const stepProps = this.state.steps[this.state.currentStep];
    const actionItem = this.state.profileActionItems.filter(a => a.view === stepProps.name)[0];

    const isDataLoaded = this.state.profileActionItems.length > 0;
    const phraseData = stepProps.phraseData();

    return (
      <div>
        <ScrollAwareContext>
          <ScrollAwareAppNavStep
            submitDisabled={!this.stepIsValid() || this.state.isSubmitting}
            cancel={stepProps.cancel}
            goBack={stepProps.goBack}
            next={this.submitProfileChanges}
            onDisabledClick={stepProps.onDisabledClick}
            color="white"
            stepTotal={this.state.steps.length - 1}
            currentStep={this.state.currentStep}
          />

          <ContentFrame>
            <ContentLoader isContentLoaded={isDataLoaded} blockRenderUntilLoaded isFixed>
              <stepProps.content
                title={phraseData.title}
                profileData={this.state.profile.data}
                submitForm={this.submitProfileChanges}
                onChange={this.onChangeProfile}
                submitErrorText={this.state.submitErrorText}
                fieldErrorName={this.state.fieldErrorName}
                clearViewErrors={this.props.dismissNotification}
                clearFieldErrors={this.clearFieldErrors}
                defaultOtherGenderText={this.state.defaultOtherGenderText}
                initialBirthSexId={this.state.profile.getBirthSexId()}
                phraseData={phraseData}
                isInitialSetup={true}
                allowWithdraw={true}
                actionItem={actionItem}
                profileActionItems={this.state.profileActionItems}
                isNewProfile={true}
                onAccept={stepProps.next || this.submitProfileChanges}
                onWithdrawConsent={stepProps.onWithdrawConsent}
              />
            </ContentLoader>

          </ContentFrame>
        </ScrollAwareContext>
      </div>
    );
  }
}
DependentProfileCreation.propTypes = {
  intl: PropTypes.object.isRequired,
  submitProfileChanges: PropTypes.func.isRequired,
  requestProfile: PropTypes.func.isRequired,
  genderIdMap: PropTypes.object.isRequired,
  cancelCreation: PropTypes.func,
  finishProfileCreation: PropTypes.func,
  resetAppMenu: PropTypes.func,
  currentColorTheme: PropTypes.object,
  loadInitialData: PropTypes.func,
  mainProfile: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
  showHintNotification: PropTypes.func,
  dismissNotification: PropTypes.func,
  isNotificationShown: PropTypes.bool,
  showSystemModal: PropTypes.func,
  isResidenceStateRequired: PropTypes.func.isRequired,
};

export default DependentProfileCreation;
